/**
 * SMS Templates Zustand Store
 *
 * Manages all state for the SMS Delivery Templates section including:
 * - Template list for document and form types
 * - CRUD operations for SMS templates
 * - Set-default functionality
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  SmsTemplate,
  SmsTemplateType,
  CreateSmsTemplateDto,
  UpdateSmsTemplateDto,
} from '@respectly/shared-types';

// ============================================
// State Interface
// ============================================

export interface SmsTemplatesState {
  // List
  templates: SmsTemplate[];
  isLoadingList: boolean;
  listError: string | null;

  // Action States
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isSettingDefault: string | null; // template id being set as default
  actionError: string | null;

  // Actions
  loadTemplates: () => Promise<void>;
  createTemplate: (dto: CreateSmsTemplateDto) => Promise<string | null>;
  updateTemplate: (id: string, dto: UpdateSmsTemplateDto) => Promise<boolean>;
  deleteTemplate: (id: string) => Promise<boolean>;
  setDefault: (id: string) => Promise<boolean>;
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  templates: [] as SmsTemplate[],
  isLoadingList: false,
  listError: null as string | null,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isSettingDefault: null as string | null,
  actionError: null as string | null,
});

// ============================================
// Store Implementation
// ============================================

export const useSmsTemplatesStore = create<SmsTemplatesState>((set, get) => ({
  ...getInitialState(),

  loadTemplates: async () => {
    set({ isLoadingList: true, listError: null });
    try {
      const api = getApiClient();
      const templates = await api.get<SmsTemplate[]>(API_ENDPOINTS.SUPER_ADMIN.SMS_TEMPLATES.LIST);
      set({ templates, isLoadingList: false });
    } catch (error) {
      set({
        isLoadingList: false,
        listError: error instanceof Error ? error.message : 'Failed to load SMS templates',
      });
    }
  },

  createTemplate: async (dto: CreateSmsTemplateDto): Promise<string | null> => {
    set({ isCreating: true, actionError: null });
    try {
      const api = getApiClient();
      const created = await api.post<SmsTemplate>(API_ENDPOINTS.SUPER_ADMIN.SMS_TEMPLATES.CREATE, dto);
      set({ isCreating: false });
      await get().loadTemplates();
      return created.id;
    } catch (error) {
      set({
        isCreating: false,
        actionError:
          error instanceof Error ? error.message : 'Failed to create SMS template',
      });
      return null;
    }
  },

  updateTemplate: async (id: string, dto: UpdateSmsTemplateDto): Promise<boolean> => {
    set({ isUpdating: true, actionError: null });
    try {
      const api = getApiClient();
      await api.put(API_ENDPOINTS.SUPER_ADMIN.SMS_TEMPLATES.UPDATE(id), dto);
      set({ isUpdating: false });
      await get().loadTemplates();
      return true;
    } catch (error) {
      set({
        isUpdating: false,
        actionError:
          error instanceof Error ? error.message : 'Failed to update SMS template',
      });
      return false;
    }
  },

  deleteTemplate: async (id: string): Promise<boolean> => {
    set({ isDeleting: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.SMS_TEMPLATES.DELETE(id));
      set((state) => ({
        templates: state.templates.filter((t) => t.id !== id),
        isDeleting: false,
      }));
      return true;
    } catch (error) {
      set({
        isDeleting: false,
        actionError:
          error instanceof Error ? error.message : 'Failed to delete SMS template',
      });
      return false;
    }
  },

  setDefault: async (id: string): Promise<boolean> => {
    set({ isSettingDefault: id, actionError: null });
    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.SMS_TEMPLATES.SET_DEFAULT(id), {});
      set({ isSettingDefault: null });
      await get().loadTemplates();
      return true;
    } catch (error) {
      set({
        isSettingDefault: null,
        actionError:
          error instanceof Error ? error.message : 'Failed to set default SMS template',
      });
      return false;
    }
  },

  clearError: () => {
    set({ listError: null, actionError: null });
  },

  reset: () => {
    set(getInitialState());
  },
}));

// ============================================
// Selector Hooks
// ============================================

/** Get templates for document type */
export const useDocumentSmsTemplates = () =>
  useSmsTemplatesStore((state) => state.templates.filter((t) => t.type === 'document'));

/** Get templates for form type */
export const useFormSmsTemplates = () =>
  useSmsTemplatesStore((state) => state.templates.filter((t) => t.type === 'form'));

/** Get the default template for a given type */
export const useDefaultSmsTemplate = (type: SmsTemplateType) =>
  useSmsTemplatesStore((state) =>
    state.templates.find((t) => t.type === type && t.isDefault)
  );

/** Get loading state */
export const useSmsTemplatesLoading = () =>
  useSmsTemplatesStore(
    (state) =>
      state.isLoadingList ||
      state.isCreating ||
      state.isUpdating ||
      state.isDeleting ||
      state.isSettingDefault !== null
  );
